// ADR 5.9.26u §3/§4 — one table, three widths.
//
// The chrome is chosen by `navChrome`, which has exactly the two values the
// phones' table has; `sizeClass` then decides whether the `rail` verdict is
// drawn as a rail or as a sidebar (§3). Nothing here compares a number, and
// nothing here consults Tailwind's `sm:` screen: a breakpoint in a class string
// would be a second copy of 600.
//
// **The `<Outlet>` is rendered once**, outside the chrome switch and always at
// the same position among its siblings, so React reconciles it rather than
// remounting it: a half-typed answer in an exercise survives a window resize
// that swaps a bottom bar for a rail (AC3). The three children of the frame are
// always three — `null` holds the place of the chrome that is not showing —
// because React matches siblings by index. The outer column above the frame
// keeps the same rule with three null-capable slots: `HeaderSlot` (8.9.26i),
// `TopStripSlot` (30.9.26l: the consent strip, else 25.9.26g's install strip)
// and `OfflineBannerSlot` (23.9.26v), in that
// order, between `SkipLink` and the body row.
//
// §4's two-pane is the one place the outlet does move, and it has to: at
// `list_detail` on the plan routes the detail *is* the right column. That is a
// deliberate remount at a boundary crossing, not an accident, and it changes no
// URL — the route table is untouched by this file (D4: no other route is
// two-pane this release).
//
// ADR 28.9.26e §8 — on an immersive route (`navVisibility.ts`) both nav slots
// render `null`, which they already could, so the outlet's index never moves;
// only the nav hides, never the header.

import { lazy, Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useMatch } from 'react-router-dom'

import { gated } from '../app/wasmGate'
import { Header } from './Header'
import { ShellBody } from './ShellBody'
import { emitWindowObservation, setLayoutSpread } from './analytics'
import { BottomNav } from './nav/BottomNav'
import { Rail } from './nav/Rail'
import { Sidebar } from './nav/Sidebar'
import { destinationsFor, type Destination } from './nav/destinations'
import { useNavVisibility } from './navVisibility'
import { useCollapsedChrome } from './useCollapsedChrome'
import { PAGED } from './spread/readSpread'
import { PageWindowClass, WindowClassProvider, useSpreadValue, useWindowClassValue } from './windowClassContext'
import { useAuthSession } from '../auth/session'
import { ConsentReconcile } from '../consent/ConsentReconcile'
import { TopStripSlot } from '../consent/TopStripSlot'
import { CompanionProviders } from '../features/companion/CompanionProviders'
import { TutorFab } from '../features/tutor/dock/TutorFab'
import { OfflineBannerSlot } from '../offline/OfflineBanner'
import { ReminderReconcile } from '../features/reminders/ReminderReconcile'
import { UpdateToast } from '../pwa/UpdateToast'
import { FOCUS_RING } from '../ui/Button'
import { SnackbarHost } from '../ui/feedback'

/**
 * ADR 8.9.26i §4 / 16.9.26n §2 — where the sidebar's collapse is remembered.
 *
 * `Sidebar` and `Rail` are **siblings** chosen by `LeadingChrome`, and the
 * width that chooses between them is computed here. A component cannot swap
 * itself for its sibling, so the state and its persistence live in this file
 * — the one 6.9.26p AC6 already exempts, so no size-class slug moves. The
 * list cell's twin lives with `ShellBody` (16.9.26r moved it there), and the
 * hook itself is `useCollapsedChrome.ts`.
 */
const SIDEBAR_COLLAPSE_KEY = 'lingara.sidebar.collapsed'

// ADR 17.10.26g §3 — the coach layer asks its gate of the wasm, so it comes in
// through the gate, and only signed in: a guest's boot never fetches it.
// The slot is exported for `coachHost.test.tsx` (AC13).
const CoachHost = lazy(async () => ({ default: await gated(() => import('../features/coach/CoachHost'), 'CoachHost') }))

export function CoachHostSlot() {
  if (useAuthSession().status !== 'signedIn') return null
  return <Suspense fallback={null}><CoachHost /></Suspense>
}

/**
 * The rail's two drawings, or nothing when the bar is at the foot instead.
 *
 * ADR 8.9.26i §4 — collapsing the sidebar renders the **existing `Rail`**, not
 * a fourth icon-only drawing: `nav/destinations.ts` makes every glyph
 * `aria-hidden`, so a genuinely icon-only sidebar would leave four
 * destinations with no accessible name at all. `Rail` keeps its labels beneath
 * the icons, so there is nothing to fix — and 5.9.26u's rule that "the table
 * has two values and so does the web" survives.
 */
function LeadingChrome({
  show,
  wide,
  expandable,
  items,
  onCollapse,
}: {
  show: boolean
  wide: boolean
  /** The rail is standing in for a collapsed sidebar, so it offers the way back. */
  expandable: boolean
  items: Destination[]
  onCollapse: (collapsed: boolean) => void
}) {
  if (!show) return null
  return wide ? (
    <Sidebar items={items} onCollapse={() => onCollapse(true)} />
  ) : (
    <Rail items={items} onExpand={expandable ? () => onCollapse(false) : undefined} />
  )
}

/**
 * ADR 15.9.26ah §6 — the first focusable thing on every page.
 *
 * At desk width a keyboard user on `/plans/:id` otherwise tabs through the
 * settings gear, four nav links and the collapse control before reaching
 * the page. The `href` is kept for semantics and no-JS; the handler is what
 * moves focus, for two reasons. Browsers disagree on whether a fragment jump
 * focuses its target and jsdom does not (it updates `location.hash` and
 * nothing else). And a bare fragment navigation would push `/plans#main`
 * through the data router — a new location for `ScrollRestoration` to scroll
 * to the top of, the opposite of what a skip link is for. `preventDefault()`
 * keeps the URL and the scroll where they are and lands focus on `<main>`,
 * which carries `tabIndex={-1}` so it can take it.
 *
 * Unconditional, so the frame's sibling-position invariant (the `<Outlet>`
 * is never renumbered) holds; it renders on `/s/:token` and `/signin` too,
 * which is right, since those pages have a `<main>` to skip to.
 */
function SkipLink() {
  const { t } = useTranslation()
  return (
    <a
      href="#main"
      onClick={(event) => {
        event.preventDefault()
        document.getElementById('main')?.focus()
      }}
      className={
        'sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-20 ' +
        `rounded-control bg-accent px-3 py-2 text-label-lg text-on-accent ${FOCUS_RING}`
      }
    >
      {/* ADR 24.9.26l §4 — its own label: `coach_mark_skip` is just "Skip". */}
      {t('a11y_skip_to_content')}
    </a>
  )
}

/**
 * ADR 7.10.26v §4 — the body row, extracted from `AdaptiveFrame` (its 50-line
 * cap) at the same outer-column index, its three children at theirs.
 *
 * The leading chrome reads the whole window; `<main>` re-provides the leading
 * page's class, always (one element type at every spread), so every page
 * composes for its half. Beside a companion page there is no sidebar (it would
 * take 16rem from a page classified by its half) and no plans split: the
 * single-column branch is what `ShellBody` draws whenever the spread is paged,
 * even when a forced split hands the leading page `list_detail`.
 */
function BodyRow() {
  const { sizeClass, navChrome } = useWindowClassValue()
  const spread = useSpreadValue()
  const paged = PAGED[spread.layout]
  const { rail, showNav } = useNavVisibility(navChrome)
  const [collapsed, setCollapsed] = useCollapsedChrome(SIDEBAR_COLLAPSE_KEY, 'sidebar')
  const atDeskWidth = sizeClass === 'expanded' && !paged
  const items = destinationsFor(useLocation().pathname, useAuthSession().flags) // 30.9.26s §2, 30.9.26y §1
  return (
    <div className={`flex min-h-0 flex-1 ${rail ? 'flex-row' : 'flex-col'}`}>
      <LeadingChrome
        show={rail && showNav}
        wide={atDeskWidth && !collapsed}
        expandable={atDeskWidth && collapsed}
        items={items}
        onCollapse={setCollapsed}
      />
      <main id="main" tabIndex={-1} className="min-w-0 flex-1 outline-none">
        <PageWindowClass value={spread.leading}>
          <ShellBody listDetail={spread.leading.paneLayout === 'list_detail' && !paged} />
        </PageWindowClass>
      </main>
      {rail || !showNav ? null : <BottomNav items={items} />}
    </div>
  )
}

/**
 * The frame itself, inside the provider so it reads the published class.
 *
 * ADR 6.9.26p §2/§3 — both shell emits live in **one** effect keyed on the
 * object `useWindowClassValue()` returns. The hook guarantees that object is
 * referentially stable until one of its three answers changes, so this effect
 * runs exactly at the moments worth reporting: once at mount (the launch) and
 * once per answer change (a crossing, when it is one).
 *
 * ADR 7.10.26v §6 — the spread is pushed from its own effect, declared above
 * the observation's so the launch sees it; a spread-only change emits nothing.
 */
function AdaptiveFrame() {
  const windowClass = useWindowClassValue()
  const spread = useSpreadValue()

  useEffect(() => {
    setLayoutSpread(spread.answered ? spread.layout : null, spread.preference)
  }, [spread])
  useEffect(() => {
    emitWindowObservation(windowClass)
  }, [windowClass])

  return (
    // ADR 16.9.26r §1/§2 / 7.10.26w §7 — the tutor providers and the app card's
    // wrap the outer column, once: the companion cell is `ShellBody`'s and a
    // page cannot open a cell it does not own (8.9.26i §4).
    <CompanionProviders>
        {/* ADR 8.9.26i §4 / 25.9.26g §4 — the header and the install strip are
            **conditional**, so neither is a conditional sibling of the frame's
            three children: that would renumber them and remount the `<Outlet>`,
            the invariant at the top of this file. Each is a slot of this outer
            column instead — always present, `null` when not showing.
            `min-h-screen` lives here, or the shell composes past one viewport. */}
        <div className="relative flex min-h-screen flex-col bg-surface font-sans text-ink">
          <SkipLink />
          <HeaderSlot />
          <TopStripSlot />
          <OfflineBannerSlot />
          <BodyRow />
          {/* ADR 16.9.26r §2 — appended after the three that exist, so no
              index moves; `null` off the FAB's routes. */}
          <TutorFab />
        </div>
    </CompanionProviders>
  )
}

/**
 * ADR 8.9.26i §3 — the header's two conditions, and why neither alone is right.
 *
 * *Not "inside `RequireSignedIn`"*: that guard is a **pathless** route below
 * the shell, so its `<Outlet/>` lands inside `ShellBody`'s content column — it
 * cannot mount a full-width row above the chrome, and nothing publishes "am I
 * inside that guard" for this frame to read. *Not a session read alone*:
 * `/s/:token` is reachable while signed in, deliberately, so that spelling
 * would put app chrome on a shared plan for exactly the readers most likely to
 * open one (AC4). *Not a path match alone*: `/signin` and `/register` are also
 * children of `Shell` and outside every guard, and an account menu above a
 * sign-in form is nonsense.
 *
 * `useMatch` is the mechanism `ShellBody` already uses, so the route table
 * stays 5.9.26q's.
 *
 * ADR 7.10.26ad §4 — it also paints while a token-holding tab is `loading`,
 * since nothing in it needs a confirmed identity; the auth doors are then
 * matched by path, because the status no longer keeps chrome off them.
 */
function HeaderSlot() {
  const { status } = useAuthSession()
  const onShared = useMatch('/s/:token') !== null
  const onSignIn = useMatch('/signin') !== null
  const onRegister = useMatch('/register') !== null // two calls: `useMatch` takes one pattern, and neither may be skipped
  const onAuthDoor = onSignIn || onRegister
  const show = status === 'signedIn' || (status === 'loading' && !onAuthDoor)
  if (!show || onShared) return null
  return <Header />
}

/**
 * The responsive frame every route inside `/` renders into.
 *
 * ADR 6.9.26p §1 — the provider wraps the frame, so this file holds the app's
 * only `useWindowClass()` call (through `WindowClassProvider`) and every other
 * surface that needs the class reads the same object the shell laid out with.
 * ADR 29.9.26al §4 — the snackbar host is one of those surfaces, so it is here.
 */
export function AdaptiveShell() {
  return (
    <WindowClassProvider>
      <AdaptiveFrame />
      {/* ADR 23.9.26y §6 — renders nothing; here rather than beside
          `InstallStripSlot` because `AdaptiveFrame` is at its line budget. */}
      <ReminderReconcile />
      {/* ADR 30.9.26l §3 — renders nothing; the row again at sign-in. */}
      <ConsentReconcile />
      {/* ADR 17.10.26g §3 — before the snackbars, and below them (z-20 < z-30). */}
      <CoachHostSlot />
      {/* ADR 29.9.26al §4 — appended after the frame, so no slot of it moves. */}
      <SnackbarHost pinned={<UpdateToast />} />
    </WindowClassProvider>
  )
}
